import { useState } from 'react'
import { ResponseChoice } from '@genoa/domain'
import { OverwriteCreateResponse, useOverwriteCreate } from '@genoa/middle-end'
import { toCents } from '@genoa/utils'

import { useAuthState } from '../../../../../../contexts'
import { useDefaultErrorModals } from '../../../../../../hooks/error-handling/use-default-error-modals'
import { useAccount } from '../../../../../../hooks/use-account'
import { useCustomerStatusState } from '../../../../../../modules/flex2/use-state'
import { useLogger } from '../../../../../../providers'

export const useSubmitOverwriteCreate = () => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const loggerV2 = useLogger('useSubmitOverwriteCreate')
  const { billerConnection } = useAccount()
  const [, overwriteCreate] = useOverwriteCreate()
  const { customerStatus } = useCustomerStatusState()
  const { uid } = useAuthState()
  const { displayDefaultErrorModal, displayTooManyTriesModal } = useDefaultErrorModals()

  const submit = async (
    responseChoice: ResponseChoice,
    rentPaymentAmount: string,
    onSubmitOverwriteCreate: (response: OverwriteCreateResponse, rentPaymentAmount: string) => void
  ) => {
    try {
      const billTransactionId = customerStatus?.bill_transaction_id
      const billerAccountPublicId = billerConnection?.biller_account_public_id

      setIsSubmitting(true)

      if (!uid || !billTransactionId || !billerAccountPublicId) {
        return
      }

      const { data, status } = await overwriteCreate({
        billerAccountPublicId,
        billTransactionId,
        customerId: uid,
        rent_amount: toCents(rentPaymentAmount),
        response_choice: responseChoice,
      })
      const errorMessage = data?.message
      if (status === 200 && data && (data.overwrite_public_id || responseChoice === ResponseChoice.BEHIND_ON_RENT)) {
        return onSubmitOverwriteCreate(data, rentPaymentAmount)
      } else if (status === 200) {
        loggerV2.error('BalanceOutOfRangeContainer-useSubmitOverwriteCreate', 'Overwrite public id not provided')
        displayDefaultErrorModal()
      } else if (status === 429) {
        loggerV2.error('BalanceOutOfRangeContainer-useSubmitOverwriteCreate', 'Rate limit status code: 429')
        displayTooManyTriesModal()
      } else if (status === 422 && errorMessage) {
        loggerV2.error('BalanceOutOfRangeContainer-useSubmitOverwriteCreate', 'Rate limit status code: 422')
        displayDefaultErrorModal(errorMessage)
      } else {
        loggerV2.error('BalanceOutOfRangeContainer-useSubmitOverwriteCreate', `Non 200 status-code: ${status}`)
        displayDefaultErrorModal()
      }
    } catch (error) {
      const message = error instanceof Error ? error?.message : 'An unexpected error occured'
      loggerV2.error('BalanceOutOfRangeContainer-useSubmitOverwriteCreate', `Unhandled error: ${message}`)
      displayDefaultErrorModal()
    } finally {
      setIsSubmitting(false)
    }
  }

  return {
    isSubmitting,
    submit,
  }
}
